/** The opening page of an invoice register, named once so the background warm,
 *  Sales invoices and the Purchases overview share one cache entry per actor and
 *  workspace. The server pages by id, not by issue date, so a page is drawn
 *  only when it is the whole register: a longer one loads in full when opened,
 *  as before, and the warm leaves it alone until its entry lapses. */

import { INVOICE_PAGE_SIZE, InvoicesService } from '../../services/InvoicesService';
import {
  currentBackgroundReadScope,
  forgetBackgroundRead,
  peekBackgroundRead,
  readInBackground,
  rememberBackgroundRead,
  type BackgroundReadScope,
} from '../../services/BackgroundReadCache';
import type { Invoice, InvoiceType } from '../../types/accounting';

export interface InvoiceRegisterOpening { invoices: Invoice[]; complete: boolean }

export const invoiceRegisterOpeningRead = (workspaceId: string, type: InvoiceType) => ({
  name: `invoices/${type}/opening`,
  load: async (): Promise<InvoiceRegisterOpening> => {
    const page = await InvoicesService.firstPage({ workspaceId, type });
    // Part of a longer register is never drawn, so it is not kept either.
    return page.complete ? page : { invoices: [], complete: false };
  },
});

function scopeFor(actorId: string | undefined, workspaceId: string): BackgroundReadScope | null {
  const scope = workspaceId ? currentBackgroundReadScope(workspaceId) : null;
  return scope && (!actorId || scope.actorId === actorId) ? scope : null;
}

/** The whole register when the cache holds it, for the signed-in actor only. */
export function cachedInvoiceRegister(actorId: string | undefined, workspaceId: string, type: InvoiceType): Invoice[] | undefined {
  const opening = peekBackgroundRead<InvoiceRegisterOpening>(scopeFor(actorId, workspaceId), invoiceRegisterOpeningRead(workspaceId, type).name)?.data;
  return opening?.complete ? opening.invoices : undefined;
}

/** Keeps what a register just loaded in full: its rows when they fit one page, else only that they do not. */
export function rememberInvoiceRegister(actorId: string | undefined, workspaceId: string, type: InvoiceType, invoices: Invoice[]): void {
  const scope = scopeFor(actorId, workspaceId);
  if (!scope) return;
  const complete = invoices.length <= INVOICE_PAGE_SIZE;
  rememberBackgroundRead(scope, invoiceRegisterOpeningRead(workspaceId, type).name, { invoices: complete ? invoices : [], complete });
}

export function forgetInvoiceRegister(actorId: string | undefined, workspaceId: string, type: InvoiceType): void {
  const scope = scopeFor(actorId, workspaceId);
  if (scope) forgetBackgroundRead(scope, invoiceRegisterOpeningRead(workspaceId, type).name);
}

/** The warm's task: one page, and none for a register already known to be longer. */
export function warmInvoiceRegister(scope: BackgroundReadScope, workspaceId: string, type: InvoiceType, maxAgeMs: number): Promise<unknown> {
  const read = invoiceRegisterOpeningRead(workspaceId, type);
  if (peekBackgroundRead<InvoiceRegisterOpening>(scope, read.name)?.data.complete === false) return Promise.resolve();
  return readInBackground(scope, read.name, read.load, { maxAgeMs });
}
